import { useViewEvent } from '@stitch-fix/event-reporter';
import { Box, getResponsiveValue, Text, useScreenSize } from '@stitch-fix/mode-react';
import { Link } from '@stitch-fix/sf-next';
import type { ModuleCategoryCard } from '../CategoryCardsModule/types';
import styles from './AlphaSortGrid.module.scss';

/** V2 event `name` for the surface rendering this grid. */
type LinkName = 'category_card' | 'brand_category_link';
interface CardLinkProps extends ModuleCategoryCard {
  name: LinkName;
}
const CategoryCardLink = ({
  categoryId,
  href,
  title,
  name
}: CardLinkProps) => {
  const {
    EventProvider,
    ref,
    reportSelectEvent
  } = useViewEvent({
    schema: 'category_view',
    name,
    category_id: categoryId
  });
  return <EventProvider>
      <Box className={styles.item} ref={ref}>
        <Text family="regular" scale="3" height="tall">
          <Link variant="inherit" underline="inverse" href={href} onClick={() => reportSelectEvent({
          action_name: 'navigate_to_category',
          category_id: categoryId,
          name,
          schema: 'category_select'
        })}>
            {title}
          </Link>
        </Text>
      </Box>
    </EventProvider>;
};
interface Props {
  /** Cards pre-grouped by A–Z section title, in display order. */
  groups: [string, ModuleCategoryCard[]][];
  /** V2 event `name` for the per-card view/select events. */
  linkName: LinkName;
}

/**
 * Sectioned A–Z grid of category links. Presentational: the caller groups the
 * cards (hrefs differ per surface) and owns any module-level view event.
 */
const AlphaSortGrid = ({
  groups,
  linkName
}: Props) => {
  const screenSize = useScreenSize();
  // Columns default to 2, grow to 3 at larger screen widths.
  const columnCount = getResponsiveValue(screenSize, {
    sm: 2,
    lg: 3
  }, 2);
  return <Box className={styles.container}>
      {groups.map(([sectionTitle, sectionCards]) => <Box className={styles.section} key={sectionTitle}>
          <Box className={styles.sectionTitle} id={sectionTitle} data-testid="section-title">
            {/* letter of the alphabet heading */}
            <Text className={styles.title}>{sectionTitle}</Text>
          </Box>
          {/* the grid of category names underneath each letter */}
          <Box className={styles.grid} style={{
        // Fill columns left-to-right; every column shares the tallest
        // column's height. Default to 5 rows before spilling to the next.
        gridTemplateRows: `repeat(${Math.max(Math.min(sectionCards.length, 5), Math.ceil(sectionCards.length / columnCount))}, 1fr)`,
        gridTemplateColumns: `repeat(${columnCount}, 1fr)`
      }}>
            {sectionCards.map(card => <CategoryCardLink key={card.categoryId} {...card} name={linkName} />)}
          </Box>
        </Box>)}
    </Box>;
};
export default AlphaSortGrid;